import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOfType,
  string,
} from "prop-types";
import { forwardRef, useCallback } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";

import styled from "styled-components/macro";
import { glow } from "theme";

import { useCloseNavIfOpen } from "NavBar/stateHooks";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrNumericIndicator from "UI/FnrNumericIndicator";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrTypography from "UI/FnrTypography";
import { colors, navAccentColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import externalNavigate from "Utilities/externalNavigate";
import dynBackgroundColor from "Utilities/interpolations/dynBackgroundColor";
import dynBorderRadius from "Utilities/interpolations/dynBorderRadius";
import dynFlexDirection from "Utilities/interpolations/dynFlexDirection";
import dynPadding from "Utilities/interpolations/dynPadding";
import flash from "Utilities/interpolations/flash";
import { addLeadingSlash } from "Utilities/stringTransformers";

import useIsSelected from "./useIsSelected";

const typographyProps = {
  type: "copy",
  noWrap: true,
  color: "textLight",
  fontSize: "sm",
  shadow: true,
};

const menuTypographyProps = {
  ...typographyProps,
  fontSize: "md",
  type: undefined,
};

const TypographyRelay = ({ children }) => {
  if (typeof children === "string")
    return <FnrTypography {...typographyProps}>{children}</FnrTypography>;
  return children;
};

TypographyRelay.propTypes = {
  color: string,
  children: oneOfType([string, node]),
};

/**
 * @typedef {object} NavLinkProps
 * @property {string} to - The destination path (relative for internal, full URL for external)
 * @property {boolean} [external] - Whether the link navigates to an external URL
 * @property {string} [icon] - Icon identifier to display
 * @property {string|React.ReactNode} [children] - Link label content
 * @property {number} [paddingX=6] - Horizontal padding in pixels
 * @property {number} [paddingY=8] - Vertical padding in pixels
 * @property {number} [count] - Notification count to display
 * @property {boolean} [showCount] - Whether to show the notification indicator
 * @property {string} [navSectionKey] - Key for selecting accent colors from navAccentColors
 * @property {number} [borderRadius=8] - Border radius in pixels
 * @property {number} [tapScale] - Scale factor on tap/click (disabled when selected)
 * @property {string} [flexDirection="column"] - Flex direction for icon/label layout
 * @property {string} [iconSize="xl"] - Size of the icon
 * @property {function} [sideEffect] - Optional callback executed after navigation
 * @property {boolean} [targetBlank] - Whether external links open in a new tab
 * @property {boolean} [isFlashing] - Whether to apply flash animation
 * @property {string[]} [alsoSelectedOn] - Other paths that show this link as selected, for a page with tabs the link doesn't go to
 */

/**
 * A navigation link component for the NavBar that handles both internal and external navigation.
 * Displays an optional icon, label, and notification count indicator.
 *
 * @type {React.ForwardRefExoticComponent<NavLinkProps & React.RefAttributes<HTMLElement>>}
 */
const NavLink = forwardRef(
  (
    {
      to,
      external,
      icon,
      children,
      paddingX = 6,
      paddingY = 8,
      count,
      showCount,
      navSectionKey,
      borderRadius = 8,
      tapScale,
      flexDirection = "column",
      iconSize = "xl",
      sideEffect,
      targetBlank,
      isFlashing,
      omitNumericIndicator,
      disableWhenSelected,
      alsoSelectedOn,
      ...rest
    },
    ref
  ) => {
    const closeNavIfOpen = useCloseNavIfOpen();
    const normalizedTo = external ? to : addLeadingSlash(to);
    // [] rather than undefined: useRouteMatch() with no path returns the
    // parent match, which is always truthy
    const alsoSelectedMatch = useRouteMatch(alsoSelectedOn ?? []);
    const isSelected =
      useIsSelected(normalizedTo) || Boolean(alsoSelectedMatch);
    const { pathname, search } = useLocation();

    const selectedColors = navAccentColors[navSectionKey] || [];

    const handleClick = useCallback(() => {
      const browserPathname =
        window.location?.pathname?.toLowerCase() ?? pathname.toLowerCase();
      const normalizedDestination = normalizedTo.toLowerCase();
      const isCurrentDestination =
        browserPathname === normalizedDestination ||
        browserPathname.startsWith(`${normalizedDestination}/`);

      if (disableWhenSelected && isCurrentDestination) {
        return;
      }

      if (external) {
        externalNavigate(normalizedTo, targetBlank ? "_blank" : "_self");
      }
      // Navigate unless we are already on the exact destination. A re-click
      // from a sub-route (e.g. /giving/campaigns/123 → Campaigns) returns to
      // the base page even though the link is styled as selected.
      else if (pathname !== normalizedTo || search) {
        window.fnReactBus.goToUrl(normalizedTo, true);
        closeNavIfOpen();
      }
      if (sideEffect) sideEffect();
    }, [
      closeNavIfOpen,
      disableWhenSelected,
      external,
      normalizedTo,
      pathname,
      search,
      sideEffect,
      targetBlank,
    ]);

    const flashColor = selectedColors[0]
      ? addOpacity(selectedColors[0], 0.5)
      : undefined;
    return (
      <>
        <FnrShineButton
          role="link"
          aria-current={isSelected ? "page" : undefined}
          data-href={normalizedTo}
          onClick={handleClick}
          style={{ position: "relative" }}
          borderRadius={borderRadius}
          tapScale={isSelected ? 1 /* (off) */ : tapScale}
          ref={ref}
          {...rest}
        >
          <ContentWrapper
            $isFlashing={isFlashing}
            $flashToColor={flashColor}
            $borderRadius={borderRadius}
            $backgroundColor={isSelected ? selectedColors[1] : undefined}
            $glowColor={selectedColors[0]}
            $noGlow={!isSelected}
          >
            {/* Using internal FnrTappyShrink so that we don't scale the background color */}
            <SFnrTappyShrink
              $padding={`${paddingY}px ${
                flexDirection === "row" ? paddingX + 8 : paddingX
              }px ${paddingY}px ${paddingX}px`}
              $flexDirection={flexDirection}
              scale={isSelected ? 0.98 : 1 /* off */}
            >
              {icon && (
                <FnrContainedIcon
                  shadow
                  color="textLight"
                  icon={icon}
                  size={iconSize}
                />
              )}
              <TypographyRelay>{children}</TypographyRelay>
            </SFnrTappyShrink>
          </ContentWrapper>
          {!isSelected && (
            <HiddenLink
              aria-label="hidden link to allow right-click menu options for this link"
              href={normalizedTo}
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault();
              }}
            />
          )}
        </FnrShineButton>
        {!omitNumericIndicator && (
          <RelativeBoundary>
            <FnrNumericIndicator
              color={colors.fnDkRed}
              count={count}
              show={showCount}
              right={8.5}
              top={4.5}
            />
          </RelativeBoundary>
        )}
      </>
    );
  }
);

const RelativeBoundary = styled.div`
  position: relative;
  ${({ $fullWidth }) => ($fullWidth ? "width: 100%;" : "")}
`;

const SFnrTappyShrink = styled(FnrTappyShrink)`
  position: relative;
  display: flex;
  ${dynFlexDirection}
  align-items: center;
  gap: 4px;
  /* Break up long words that cause an overflow */
  overflow-wrap: anywhere;
  ${dynPadding}
`;

const ContentWrapper = styled.div`
  ${dynBackgroundColor}
  ${glow}
  ${dynBorderRadius}
  ${flash}
  transition: background-color 300ms ease-in-out;
`;

const HiddenLink = styled.a`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

NavLink.propTypes = {
  to: string,
  icon: string,
  children: oneOfType([string, node]),
  external: bool,
  paddingX: number,
  paddingY: number,
  count: number,
  showCount: bool,
  navSectionKey: string,
  borderRadius: number,
  tapScale: number,
  flexDirection: string,
  iconSize: string,
  sideEffect: func,
  targetBlank: bool,
  isFlashing: bool,
  omitNumericIndicator: bool,
  disableWhenSelected: bool,
  alsoSelectedOn: arrayOf(string),
};

export { typographyProps, menuTypographyProps };
export default NavLink;
